/**
 * The measure cap from §7.1: content stops widening at a readable line length
 * even when the pane keeps growing.
 */

import type { CSSProperties, ElementType, HTMLAttributes, ReactNode } from "react";
import type { MeasureName } from "./breakpoints";
import { MEASURE_CAPS } from "./breakpoints";

export interface ContainerProps extends HTMLAttributes<HTMLElement> {
  readonly as?: ElementType;
  /**
   * Required rather than defaulted. §7.1 gives five different caps for five
   * different kinds of surface, and a default would mean the wrong one is the
   * one nobody notices.
   */
  readonly measure: MeasureName | "none";
  /**
   * §7.2 puts 19–20 px of horizontal padding on the compact column. Turn it off
   * only for a container nested inside one that already applies it, otherwise
   * the inset doubles.
   */
  readonly inset?: boolean;
  readonly children?: ReactNode;
  readonly "data-testid"?: string;
}

export function Container({
  as,
  measure,
  inset = true,
  className,
  style,
  children,
  ...rest
}: ContainerProps): ReactNode {
  const Element: ElementType = as ?? "div";
  const composedClassName = className === undefined ? "ff-container" : `ff-container ${className}`;
  const composedStyle: CSSProperties = {
    ...(measure === "none" ? {} : { maxWidth: `${MEASURE_CAPS[measure]}px` }),
    ...style,
  };

  return (
    <Element
      className={composedClassName}
      style={composedStyle}
      data-ff-measure={measure}
      data-ff-inset={String(inset)}
      {...rest}
    >
      {children}
    </Element>
  );
}
